"use client";

import React, { useState } from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import { useBillingCompanyContext } from "../../providers";

export interface IPaymentReminderName {
  campaign_name: string;
  market_campaign_uuid: string;
}

interface IPaymentReminderNameAutoSearchProps {
  value?: string;
  label?: string;
  disabled?: boolean;
  onSelect?: (item: IPaymentReminderName | null) => void;
  sx?: any;
}

/**
 * Autosearch for distinct reminder (campaign) names present in sent payment
 * reminder logs. Backed by /campaign/get-distinct-reminder-in-campaign.
 */
export const PaymentReminderNameAutoSearch: React.FC<IPaymentReminderNameAutoSearchProps> = ({
  value = "",
  label,
  disabled,
  onSelect,
  sx,
}) => {
  const companyOutlet = useBillingCompanyContext();

  const hasFetchedRef = React.useRef(false);

  const [options, setOptions] = useState<IPaymentReminderName[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;

  const fetchReminderNames = async (search: string) => {
    setLoading(true);
    try {
      let query = "";
      if (search) {
        query = `search=${encodeURIComponent(search)}&`;
      }
      const url = `/campaign/get-distinct-reminder-in-campaign?${query}company_outlet_uuid=${company_outlet_uuid}`;
      const res = await api.get(url);
      setOptions(res.data.data || []);
    } catch (error) {
      console.error("Error fetching reminder names:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleFocus = () => {
    if (!hasFetchedRef.current) {
      fetchReminderNames("");
      hasFetchedRef.current = true;
    }
  };

  return (
    <>
      {label && <CustomFormLabel>{label}</CustomFormLabel>}

      <Autocomplete
        sx={{
          ...sx,
          ".MuiOutlinedInput-root": {
            paddingTop: "1.5px",
            paddingBottom: "1.5px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        freeSolo
        fullWidth
        size="small"
        disabled={disabled}
        options={options}
        loading={loading}
        inputValue={inputValue}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.campaign_name || ""
        }
        isOptionEqualToValue={(option, val) =>
          option.market_campaign_uuid === val.market_campaign_uuid
        }
        onFocus={handleFocus}
        onInputChange={(_event, newValue, reason) => {
          if (reason === "input") {
            setInputValue(newValue);
            fetchReminderNames(newValue);
          }
        }}
        onChange={(_event, newValue) => {
          if (typeof newValue === "object" && newValue) {
            onSelect?.(newValue);
          } else {
            onSelect?.(null);
          }
        }}
        noOptionsText="No reminders found"
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label="Reminder Name"
            placeholder="Search reminder name..."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};
